import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  createTheme,
  ThemeProvider,
  Divider,
} from "@mui/material";
import {
  FolderOutlined,
  PeopleOutline,
  PersonOutline,
  AssignmentOutlined,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { CMSContext } from "../context/CaseManagmentContext";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import Skeleton from "@mui/material/Skeleton";
import {
  getClientsCountApi,
  getInvitationStatsApi,
  getAdvocateTasksApi,
  getTasksCreatedByUserApi,
} from "../../../services/api/caseManagementApi";

// const baseUrl = "http://localhost:7071/api";
// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#003D78",
      light: "#335F8F",
      dark: "#002A54",
    },
    secondary: {
      main: "#FF9800",
      light: "#FFAD33",
      dark: "#CC7A00",
    },
    background: {
      default: "#F5F7FA",
      paper: "#FFFFFF",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FFC107",
    },
    info: {
      main: "#2196F3",
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },
  },
});

const COLORS = ["#003D78", "#FF9800", "#4CAF50", "#F44336"];

let isDataLoading = false; // Placeholder for loading state

const StatCard = ({ title, value, icon, color }) => {
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "visible",
      }}
    >
      <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            position: "absolute",
            top: -15,
            left: 20,
            width: 60,
            height: 60,
            borderRadius: "50%",
            backgroundColor: color,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
          }}
        >
          {React.createElement(icon, { sx: { fontSize: 30, color: "white" } })}
        </Box>
        <Box sx={{ mt: 4, mb: 1 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const CaseDistributionChart = ({ data }) => {
  // Transform the data for the bar chart
  const chartData = Object.entries(data).map(([key, value]) => ({
    name: key,
    value: value,
    fill:
      key === "Not Started"
        ? COLORS[0]
        : key === "In Progress"
          ? COLORS[1]
          : COLORS[2],
  }));

  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" component="div" gutterBottom>
          Case Distribution
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ height: 200, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontFamily: "Roboto", fontSize: "14px" }}
              />
              <YAxis tick={{ fontFamily: "Roboto", fontSize: "14px" }} />
              <RechartsTooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const TaskDistributionChart = ({ data }) => {
  // Transform the data for the bar chart
  const chartData = Object.entries(data).map(([key, value]) => ({
    name: key,
    value: value,
    fill: key === "Assigned by me" ? COLORS[0] : COLORS[1],
  }));

  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" component="div" gutterBottom>
          Task Distribution
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ height: 200, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontFamily: "Roboto", fontSize: "14px" }}
              />
              <YAxis tick={{ fontFamily: "Roboto", fontSize: "14px" }} />
              <RechartsTooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const InvitationStatsChart = ({ data, loading }) => {
  // Define color scheme for different statuses
  const STATUS_COLORS = {
    total: COLORS[0],
    accepted: COLORS[1],
    resended: COLORS[2],
    pending: COLORS[3],
  };

  // Transform the data for the bar chart
  const chartData = data
    ? Object.entries(data).map(([key, value]) => ({
        name: key?.charAt(0)?.toUpperCase() + key?.slice(1),
        value: value,
        fill: STATUS_COLORS[key],
      }))
    : [];

  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ p: 3 }}>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <Typography variant="h6" component="div" gutterBottom>
            Invitation Statistics
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ height: 200, width: "100%" }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontFamily: "Roboto", fontSize: "14px" }}
                  tickFormatter={(value) =>
                    value === "Resended" ? "Resent" : value
                  }
                />
                <YAxis tick={{ fontFamily: "Roboto", fontSize: "14px" }} />
                <RechartsTooltip
                  formatter={(value) => [value, "Count"]}
                  labelFormatter={(label) =>
                    label === "Resended" ? "Resent" : label
                  }
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={30} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const DashboardContent = () => {
  const userLogin = useSelector((state) => state.userLogin);
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;
  const { cases, advocates } = useContext(CMSContext);

  const [stats, setStats] = useState({
    totalCases: {
      "Not Started": 0,
      "In Progress": 0,
      Completed: 0,
    },
    totalClients: { active: 0 },
    totalLawyers: { active: 0 },
    totalTasks: {
      current: { "Assigned by me": 0, "Assigned to me": 0 },
      overdue: { "Assigned by me": 0, "Assigned to me": 0 },
    },
  });

  // Runs once on mount — fires all API calls in parallel, updates state once when all settle
  useEffect(() => {
    const fetchDashboardStats = async () => {
      const [clientsRes, invitationsRes, tasksToMeRes, tasksByMeRes] =
        await Promise.allSettled([
          getClientsCountApi(ownerId),
          getInvitationStatsApi(ownerId),
          getAdvocateTasksApi(ownerId),
          getTasksCreatedByUserApi(ownerId),
        ]);

      setIsLoading(false);
      isDataLoading = false;
      setStats((prev) => ({
        ...prev,
        totalClients: {
          active:
            clientsRes.status === "fulfilled"
              ? clientsRes.value.data.count
              : prev.totalClients.active,
        },
        invitations:
          invitationsRes.status === "fulfilled"
            ? {
                total: invitationsRes.value.data.total,
                accepted: invitationsRes.value.data.accepted,
                resended: invitationsRes.value.data.resended,
                pending: invitationsRes.value.data.pending,
              }
            : { error: "Failed to load invitation stats" },
        totalTasks: {
          current: {
            "Assigned to me":
              tasksToMeRes.status === "fulfilled"
                ? tasksToMeRes.value.data.tasks?.length || 0
                : 0,
            "Assigned by me":
              tasksByMeRes.status === "fulfilled"
                ? tasksByMeRes.value.data.tasks?.length || 0
                : 0,
          },
          overdue: { "Assigned by me": 0, "Assigned to me": 0 },
        },
      }));
    };
    if (isDataLoading) return; // Prevent multiple simultaneous loads
    isDataLoading = true;
    fetchDashboardStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculates derived counts whenever cases or advocates update
  useEffect(() => {
    setStats((prevStats) => ({
      ...prevStats,
      totalLawyers: { active: advocates?.length || 0 },
      totalCases: {
        "Not Started":
          cases?.filter((c) => c.status === "Not Started")?.length || 0,
        "In Progress":
          cases?.filter((c) => c.status === "In Progress")?.length || 0,
        Completed: cases?.filter((c) => c.status === "Completed")?.length || 0,
      },
    }));
  }, [cases, advocates]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Total Cases"
              value={Object.values(stats.totalCases).reduce((a, b) => a + b, 0)}
              icon={FolderOutlined}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Active Clients"
              value={stats.totalClients?.active}
              icon={PeopleOutline}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Active Lawyers"
              value={stats.totalLawyers?.active}
              icon={PersonOutline}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              title="Total Tasks"
              value={Object.values(stats.totalTasks).reduce(
                (sum, obj) =>
                  sum + Object.values(obj).reduce((a, b) => a + b, 0),
                0,
              )}
              icon={AssignmentOutlined}
              color={theme.palette.error.main}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TaskDistributionChart data={stats.totalTasks.current} />
          </Grid>

          <Grid item xs={12} md={4}>
            <CaseDistributionChart data={stats.totalCases} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InvitationStatsChart
              data={stats.invitations}
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardContent;
